import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Checkbox, Input, Modal, Select, Spin, message,
} from 'antd';
import csrfHeaders from '../../../utils/csrfHeaders';
import { captureAnalytics } from '../../../actions/preload';

// Per-field settings for a NextGen document type (ENG-4052, widening the
// ENG-3966 pick-list defaults): a Medsender label, a pick-list default, a rule
// for free text, and whether AI fills the field. Automatic uploads file the
// defaults. Like the mappings, one save applies to every synced
// practice; practices that lack the type or a value are named instead of
// failing the save. The server says which inputs each field allows.

const failureMessage = (error) => error.response?.data?.error || 'Could not load the field settings. Please try again.';
const NONE = '';
const MAX_LABEL = 60;
const MAX_INSTRUCTION = 200;
const AI = 'ai';
// Automatic uploads fill required plain text with the document title, which a
// rule would reject, so the type stops filing automatically (ENG-4052 review).
const RULE_ON_REQUIRED = 'Automatic uploads of this document type will stop: they can’t fill a required field that has a rule. '
  + 'File these documents with Send to Chart.';

const names = (practices) => practices.map((practice) => practice.name).join(', ');

// The editable form of a saved setting; blanks for anything unset.
const draftFor = (setting = {}) => ({
  label: setting.label || '',
  validation: setting.validation || NONE,
  source: setting.source || NONE,
  instruction: setting.instruction || '',
});

// Only the keys that apply to the field; `{}` clears it.
const settingFor = (field, draft) => {
  const setting = {};
  const label = draft.label.trim();
  if (label) setting.label = label;
  if (field.allowed?.validation && draft.validation) setting.validation = draft.validation;
  if (!draft.source || !(field.allowed?.sources || []).includes(draft.source)) return setting;

  setting.source = draft.source;
  if (draft.source === AI && field.kind === 'date' && draft.instruction.trim()) {
    setting.instruction = draft.instruction.trim();
  }
  return setting;
};

// A pick-list's options for a select, led by the "nothing chosen" entry.
const optionsFor = (field, emptyLabel) => [
  { value: NONE, label: emptyLabel },
  ...(field.options || []).map((option) => ({ value: option.id, label: option.description })),
];

const Setting = ({ id, title, children }) => (
  <label className="manage-categories__nextgen-default" htmlFor={id}>
    <span>{title}</span>
    {children}
  </label>
);

Setting.propTypes = { id: PropTypes.string.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const NextgenFieldSettingsModal = ({
  base, documentType, isAdmin, onClose, onSaved,
}) => {
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    axios.get(`${base}/field_settings`, { params: { document_type_id: documentType.id } }).then(({ data: body }) => {
      if (!mounted.current) return;
      // Outside an event handler each set renders on its own, so the rows'
      // values come before the data that renders the rows.
      setDefaults(Object.fromEntries(body.fields.map((field) => [field.id, field.default || NONE])));
      setDrafts(Object.fromEntries(body.fields.map((field) => [field.id, draftFor(field.setting)])));
      setData(body);
    }).catch((err) => {
      if (mounted.current) setError(failureMessage(err));
    }).finally(() => {
      if (mounted.current) setBusy(false);
    });
    return () => { mounted.current = false; };
  }, [base, documentType.id]);

  const update = (fieldId, changes) => setDrafts((previous) => ({
    ...previous, [fieldId]: { ...previous[fieldId], ...changes },
  }));

  const fields = data?.fields || [];

  const save = async () => {
    // Every field goes in both maps, so a blank clears what was saved.
    const settings = Object.fromEntries(fields.map((field) => [field.id, settingFor(field, drafts[field.id])]));
    const savedDefaults = Object.fromEntries(fields.map((field) => [
      field.id, field.kind === 'pick_list' ? defaults[field.id] || NONE : NONE,
    ]));
    setBusy(true);
    captureAnalytics('nextgen_field_settings_save_requested', {
      default_count: Object.values(savedDefaults).filter(Boolean).length,
      setting_count: Object.values(settings).filter((setting) => Object.keys(setting).length > 0).length,
    });
    try {
      const response = await axios.put(`${base}/field_settings`, {
        document_type_id: documentType.id, revision: data.revision, defaults: savedDefaults, settings,
      }, { headers: csrfHeaders() });
      if (!mounted.current) return;
      const skipped = response.data.skipped || [];
      if (skipped.length) {
        message.warning(`Saved, but not for ${names(skipped)}.`);
      } else {
        message.success('NextGen field settings saved.');
      }
      onSaved();
    } catch (err) {
      if (mounted.current) message.error(failureMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const practices = data?.practices || [];
  // Saving can't reach a practice without the type; it can catch up one that has it.
  const withoutType = practices.filter((practice) => !practice.has_type);
  const lagging = practices.filter((practice) => practice.has_type && !practice.matches);
  const editable = isAdmin && Boolean(data);
  const disabled = !editable || busy;
  const footer = editable
    ? [
      <Button key="cancel" onClick={onClose} disabled={busy}>Cancel</Button>,
      <Button key="save" type="primary" onClick={save} disabled={busy}>Save settings</Button>,
    ]
    : [<Button key="close" onClick={onClose}>Close</Button>];

  const renderField = (field) => {
    const draft = drafts[field.id];
    const canUseAi = (field.allowed?.sources || []).includes(AI);
    const testId = (input) => `nextgen-setting-${input}-${field.id}`;

    return (
      <section key={field.id} className="manage-categories__nextgen-field" aria-label={field.description}>
        <div className="manage-categories__nextgen-field-name">
          {field.required ? `${field.description} (required)` : field.description}
        </div>
        <div className="manage-categories__nextgen-field-inputs">
          <Setting id={testId('label')} title="Label">
            <Input
              id={testId('label')}
              data-testid={testId('label')}
              size="small"
              maxLength={MAX_LABEL}
              placeholder={field.description}
              disabled={disabled}
              value={draft.label}
              onChange={(event) => update(field.id, { label: event.target.value })}
            />
          </Setting>
          {field.kind === 'pick_list' && (
            <Setting id={testId('default')} title="Default">
              <Select
                id={testId('default')}
                data-testid={testId('default')}
                size="small"
                showSearch
                optionFilterProp="label"
                disabled={disabled}
                value={defaults[field.id]}
                options={optionsFor(field, 'No default')}
                onChange={(value) => setDefaults((previous) => ({ ...previous, [field.id]: value || NONE }))}
              />
            </Setting>
          )}
          {field.allowed?.validation && (
            <Setting id={testId('rule')} title="Rule">
              <Select
                id={testId('rule')}
                data-testid={testId('rule')}
                size="small"
                disabled={disabled}
                value={draft.validation}
                options={[{ value: NONE, label: 'Any text' }, { value: 'integer_list', label: 'Whole numbers, comma-separated' }]}
                onChange={(value) => update(field.id, { validation: value || NONE })}
              />
            </Setting>
          )}
          {canUseAi && (
            <div className="manage-categories__nextgen-ai">
              <Checkbox
                id={testId('ai')}
                disabled={disabled}
                checked={draft.source === AI}
                onChange={(event) => update(field.id, { source: event.target.checked ? AI : NONE })}
              >
                Fill with AI
              </Checkbox>
            </div>
          )}
        </div>
        {field.required && draft.validation && (
          <Alert
            type="warning"
            showIcon
            message={RULE_ON_REQUIRED}
          />
        )}
        {draft.source === AI && field.kind === 'date' && (
          <Setting id={testId('instruction')} title="Instruction for AI (optional)">
            <Input
              id={testId('instruction')}
              data-testid={testId('instruction')}
              size="small"
              maxLength={MAX_INSTRUCTION}
              placeholder="The patient’s appointment date on this form"
              disabled={disabled}
              value={draft.instruction}
              onChange={(event) => update(field.id, { instruction: event.target.value })}
            />
          </Setting>
        )}
      </section>
    );
  };

  return (
    <Modal visible width={720} title={`Field settings · ${documentType.name}`} onCancel={onClose} footer={footer}>
      {error && <Alert type="error" showIcon message={error} />}
      {busy && !data && <Spin />}
      {data && (
        <div className="manage-categories__nextgen-defaults">
          <p className="manage-categories__row-meta">
            Send to Chart shows these labels, checks these rules and fills these fields.
            Automatic uploads file the defaults; a required field with a default no longer blocks them.
          </p>
          {fields.map(renderField)}
          {lagging.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Not applied at ${names(lagging)}. ${isAdmin ? 'Save to apply them there' : 'An organization admin can save them'}, or contact Medsender if a value is missing there.`}
            />
          )}
          {withoutType.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${names(withoutType)} ${withoutType.length === 1 ? 'doesn’t' : 'don’t'} offer ${documentType.name}, so these settings don’t apply there.`}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

NextgenFieldSettingsModal.propTypes = {
  base: PropTypes.string.isRequired,
  documentType: PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default NextgenFieldSettingsModal;
